import Link from "next/link"
import { ArrowRight } from "lucide-react"

import { Button } from "@/components/ui/button"
import { MotionDiv, blurIn, fadeInLeft } from "@/components/motion"
import { cn } from "@/lib/utils"
import {
  StoryAnatomyIsland,
  StoryComparisonCurtainIsland,
  StoryFilmstripIsland,
  StoryPathwayRibbonIsland,
  StoryStackedProofIsland,
} from "./storytelling-islands"
import {
  StoryStageBackdropIsland,
  StoryStageMagneticWrapper,
  StoryStageVisualIsland,
} from "./storytelling-stage-island"
import type {
  StoryAnatomyLayer,
  StoryComparisonFrame,
  StoryFrame,
  StoryHeroProps,
  StoryPathwayStep,
  StorySectionBaseProps,
} from "./storytelling-types"

function StoryButton({ cta, variant = "primary" }: { cta: { label: string; href: string }; variant?: "primary" | "secondary" }) {
  return (
    <Button asChild size="lg" variant={variant === "primary" ? "default" : "outline"} className={cn("h-auto rounded-button px-6 py-4 text-sm font-black uppercase tracking-[0.08em]", variant === "secondary" && "border-primary/30 bg-background text-primary hover:bg-muted")}>
      <Link href={cta.href}>{cta.label}<ArrowRight className="size-4" /></Link>
    </Button>
  )
}

export function StoryStageHero({ eyebrow, title, description, primaryCta, secondaryCta, metrics = [], productName = "Product", productLabel = "signature detail", backdropWord = "story", className }: StoryHeroProps) {
  return (
    <section className={cn("relative isolate overflow-hidden bg-background py-14 lg:min-h-screen lg:py-10", className)}>
      <StoryStageBackdropIsland word={backdropWord} />
      <div className="mx-auto grid min-h-[calc(100vh-5rem)] w-full max-w-theme content-center gap-8 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <MotionDiv variants={fadeInLeft} className="relative z-10">
          {eyebrow && (
            <MotionDiv variants={blurIn}>
              <p className="mb-4 text-sm font-black uppercase tracking-[0.24em] text-primary">{eyebrow}</p>
            </MotionDiv>
          )}
          <h1 className="max-w-4xl font-heading text-[clamp(3.1rem,5.3vw,5.7rem)] font-black uppercase leading-[0.88] tracking-normal text-primary">{title}</h1>
          {description && <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">{description}</p>}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <StoryButton cta={primaryCta} />
            {secondaryCta && <StoryButton cta={secondaryCta} variant="secondary" />}
          </div>
        </MotionDiv>
        <StoryStageVisualIsland productName={productName} productLabel={productLabel} metrics={metrics} />
      </div>
    </section>
  )
}

function StoryHeader({ eyebrow, title, description, align = "left" }: StorySectionBaseProps & { align?: "left" | "center" }) {
  return (
    <div className={cn("mb-14 max-w-5xl", align === "center" && "mx-auto text-center")}>
      {eyebrow && <p className="mb-5 text-sm font-black uppercase tracking-[0.24em] text-primary">{eyebrow}</p>}
      <h2 className="font-heading text-5xl font-black uppercase leading-[0.88] text-primary sm:text-7xl">{title}</h2>
      {description && <p className={cn("mt-6 max-w-3xl text-lg leading-8 text-muted-foreground", align === "center" && "mx-auto")}>{description}</p>}
    </div>
  )
}

export function StackedProofStorySection({ eyebrow, title, description, frames, className }: StorySectionBaseProps & { frames: StoryFrame[] }) {
  return <section className={cn("bg-background py-section", className)}><div className="mx-auto w-full max-w-theme px-4 sm:px-6 lg:px-8"><StoryHeader eyebrow={eyebrow} title={title} description={description} /><StoryStackedProofIsland frames={frames} /></div></section>
}

export function ComparisonCurtainStorySection({ eyebrow, title, description, frames, className }: StorySectionBaseProps & { frames: StoryComparisonFrame[] }) {
  return <section className={cn("bg-muted py-section", className)}><div className="mx-auto w-full max-w-theme px-4 sm:px-6 lg:px-8"><StoryHeader eyebrow={eyebrow} title={title} description={description} /><StoryComparisonCurtainIsland frames={frames} /></div></section>
}

export function AnatomyStorySection({ eyebrow, title, description, layers, className }: StorySectionBaseProps & { layers: StoryAnatomyLayer[] }) {
  return <section className={cn("bg-background py-section", className)}><div className="mx-auto w-full max-w-theme px-4 sm:px-6 lg:px-8"><StoryHeader eyebrow={eyebrow} title={title} description={description} /><StoryAnatomyIsland layers={layers} /></div></section>
}

export function PathwayRibbonStorySection({ eyebrow, title, description, steps, className }: StorySectionBaseProps & { steps: StoryPathwayStep[] }) {
  return <section className={cn("bg-muted py-section", className)}><div className="mx-auto w-full max-w-theme px-4 sm:px-6 lg:px-8"><StoryHeader eyebrow={eyebrow} title={title} description={description} /><StoryPathwayRibbonIsland steps={steps} /></div></section>
}

export function FilmstripNarrativeStorySection({ eyebrow, title, description, frames, className }: StorySectionBaseProps & { frames: StoryFrame[] }) {
  return <section className={cn("bg-background py-section", className)}><div className="mx-auto w-full max-w-theme px-4 sm:px-6 lg:px-8"><StoryHeader eyebrow={eyebrow} title={title} description={description} align="center" /><StoryFilmstripIsland frames={frames} /></div></section>
}

export const ecommerceStoryPreviewData = {
  metrics: [
    { value: "4.9", label: "rating" },
    { value: "24h", label: "ship" },
    { value: "30d", label: "trial" },
  ],
  proofFrames: [
    { eyebrow: "material", title: "Premium materials first", description: "Lead with texture, origin, and why the object feels different in hand.", metric: "100%", label: "traceable", accent: "touch" },
    { eyebrow: "fit", title: "Designed around daily use", description: "Show the use case, not just the product photograph or grid card.", metric: "12h", label: "comfort", accent: "daily" },
    { eyebrow: "trust", title: "Backed by reviews and guarantees", description: "Layer social proof and purchase reassurance before the add-to-cart moment.", metric: "4.9", label: "rating", accent: "trust" },
  ],
  comparisonFrames: [
    { eyebrow: "routine shift", title: "Replace the default", description: "Make the old routine and the new product experience visibly different.", before: "generic routine", after: "better ritual", metric: "2x", label: "use rate" },
    { eyebrow: "bundle shift", title: "Show the complete kit", description: "Reveal how a bundle solves more of the customer journey than a single SKU.", before: "one item", after: "complete kit", metric: "3", label: "pieces" },
  ],
  anatomyLayers: [
    { eyebrow: "detail one", title: "Ingredient or material", description: "Use anatomy for ingredients, fabric layers, components, or craftsmanship details.", part: "outer layer", metric: "01", label: "material" },
    { eyebrow: "detail two", title: "Performance detail", description: "Make the functional detail inspectable without forcing a technical spec table.", part: "core detail", metric: "02", label: "feature" },
    { eyebrow: "detail three", title: "Care and longevity", description: "Connect ownership, maintenance, warranty, and durability to the object.", part: "finish", metric: "03", label: "care" },
  ],
  pathwaySteps: [
    { eyebrow: "discover", title: "Find the right variant", description: "Guide the shopper from product interest into size, shade, bundle, or plan fit.", metric: "01", label: "fit" },
    { eyebrow: "compare", title: "Compare the proof", description: "Surface reviews, guarantees, materials, and delivery promises before checkout.", metric: "4.9", label: "rating" },
    { eyebrow: "purchase", title: "Make the cart obvious", description: "Turn the story into a confident purchase, subscription, or booking action.", metric: "24h", label: "ship" },
  ],
  filmFrames: [
    { eyebrow: "frame 01", title: "The problem appears", description: "Open with the customer context instead of a static product grid.", metric: "01", label: "scene" },
    { eyebrow: "frame 02", title: "The product enters", description: "Let the product become the hero object in a lifestyle sequence.", metric: "02", label: "scene" },
    { eyebrow: "frame 03", title: "The ritual sticks", description: "End with the product becoming part of a repeatable, desirable routine.", metric: "03", label: "scene" },
  ],
}
